/**
 * In-session pub-sub that routes thread activity in conversations to open
 * thread panels (docked or popped out). Thread-only replies never enter the
 * conversation buffer, so panels cannot rely on it for them.
 */

import type { PublicMessageThread } from '@adieuu/shared';
import type { DisplayMessage } from '../hooks/conversations/types';

export type ConversationThreadEvent =
  | {
      /** Someone else replied; the panel fetches and decrypts it by id. */
      kind: 'remote_reply';
      conversationId: string;
      messageId: string;
      threadRootId: string;
    }
  | {
      /** This client sent a reply (composer or media outbox); already decrypted. */
      kind: 'own_reply';
      conversationId: string;
      message: DisplayMessage;
    }
  | {
      /** The root's stored summary changed (null once no replies remain). */
      kind: 'summary';
      conversationId: string;
      rootId: string;
      summary: PublicMessageThread | null;
    }
  | {
      kind: 'edited';
      conversationId: string;
      messageId: string;
    }
  | {
      kind: 'deleted';
      conversationId: string;
      messageId: string;
    };

type Listener = (event: ConversationThreadEvent) => void;

const listeners = new Set<Listener>();

export function emitConversationThreadEvent(event: ConversationThreadEvent): void {
  for (const listener of [...listeners]) {
    try {
      listener(event);
    } catch {
      // A failing subscriber must not prevent the others from being notified.
    }
  }
}

/** Subscribes to thread events; returns an unsubscribe function. */
export function onConversationThreadEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only: clears subscribers between cases. */
export function resetConversationThreadEventsForTests(): void {
  listeners.clear();
}
